import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal, Button, Form, Message, Select } from 'semantic-ui-react';
import { Field, reduxForm } from 'redux-form';
import ValidatedFormInput from 'components/ValidatedFormInput';
import {
  showCreateDiscountRuleModal,
  showEditDiscountRuleModal,
  saveDiscountRule,
  setEditorErrorMessage,
} from 'state/discountRule/actions';
import {
  requiredFieldValidation,
  rangeValidation,
  minValueValidation,
} from 'utils/formUtils';

export const validate = values => {
  const errors = {};

  if (!values.name || values.name.trim() === '') {
    errors.name = 'Name is required';
  }

  const discountPercentageRequired = requiredFieldValidation(
    values.discount_percentage,
    'Discount percentage'
  );
  if (discountPercentageRequired) {
    errors.discount_percentage = discountPercentageRequired;
  } else {
    const rangeError = rangeValidation(values.discount_percentage, 0, 100);
    if (rangeError) {
      errors.discount_percentage = rangeError;
    }
  }

  const priorityRequired = requiredFieldValidation(values.priority, 'Priority');
  if (priorityRequired) {
    errors.priority = priorityRequired;
  } else {
    const minError = minValueValidation(values.priority, 0);
    if (minError) {
      errors.priority = minError;
    }
  }

  if (!values.sales_type) {
    errors.sales_type = 'Sale type is required';
  }

  return errors;
};

class DiscountRuleEditorModal extends React.Component {
  onClose = () => {
    const { dispatch, isEditMode, reset } = this.props;
    dispatch(setEditorErrorMessage(null));
    if (isEditMode) {
      dispatch(showEditDiscountRuleModal(false, null));
    } else {
      dispatch(showCreateDiscountRuleModal(false));
    }
    reset();
  };

  onSubmit = values => {
    const { dispatch, isEditMode, editingRule } = this.props;

    const payload = {
      name: values.name,
      discount_percentage: parseFloat(values.discount_percentage),
      priority: parseInt(values.priority, 10),
      subscription_period_months_left: values.subscription_period_months_left,
      sales_types: [values.sales_type],
    };

    if (isEditMode && editingRule) {
      payload.id = editingRule.id;
    }

    dispatch(saveDiscountRule(payload));
  };

  renderSelectField = ({ input, meta, label, options, ...rest }) => {
    const errorContent = meta.touched && !!meta.error && meta.error;
    return (
      <Form.Field error={!!errorContent} required>
        <label>{label}</label>
        <Select
          {...input}
          {...rest}
          options={options}
          value={input.value}
          onChange={(e, { value }) => input.onChange(value)}
          onBlur={() => input.onBlur(input.value)}
        />
        {errorContent && (
          <div style={{ color: '#9f3a38', marginTop: '0.25em' }}>
            {errorContent}
          </div>
        )}
      </Form.Field>
    );
  };

  render() {
    const {
      isOpen,
      isEditMode,
      isLoading,
      handleSubmit,
      pristine,
      errorMessage,
    } = this.props;

    return (
      <Modal open={isOpen} onClose={this.onClose} size="small">
        <Modal.Header>
          {isEditMode ? 'Edit Discount Rule' : 'Create Discount Rule'}
        </Modal.Header>
        <Modal.Content>
          {errorMessage && (
            <Message negative>
              <Message.Header>Error</Message.Header>
              <p>{errorMessage}</p>
            </Message>
          )}
          <Form onSubmit={handleSubmit(this.onSubmit)}>
            <Form.Field>
              <Field
                name="sales_type"
                component={this.renderSelectField}
                label="Sale Type"
                placeholder="Select sale type"
                options={[
                  { key: 'RENEWAL', text: 'Renewal', value: 'RENEWAL' },
                ]}
                disabled
                required
              />
            </Form.Field>
            <Form.Field>
              <label>Name</label>
              <Field
                name="name"
                component={ValidatedFormInput}
                placeholder="Enter discount rule name"
                required
              />
            </Form.Field>
            <Form.Field>
              <label>Discount Percentage</label>
              <Field
                name="discount_percentage"
                component={ValidatedFormInput}
                placeholder="Enter discount percentage (0-100)"
                type="number"
                step="0.01"
                required
              />
            </Form.Field>
            <Form.Field>
              <label>Priority</label>
              <Field
                name="priority"
                component={ValidatedFormInput}
                placeholder="Enter priority"
                type="number"
                required
              />
            </Form.Field>
            <Form.Field>
              <label>Months Left Rule</label>
              <Field
                name="subscription_period_months_left"
                component={ValidatedFormInput}
                placeholder="Enter number of months"
                type="number"
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={this.onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            primary
            onClick={handleSubmit(this.onSubmit)}
            disabled={pristine || isLoading}
            loading={isLoading}
          >
            {isEditMode ? 'Update' : 'Create'}
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

DiscountRuleEditorModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  editingRule: PropTypes.object,
  reset: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  invalid: PropTypes.bool.isRequired,
};

DiscountRuleEditorModal.defaultProps = {
  editingRule: null,
  errorMessage: null,
};

const mapStateToProps = (state, ownProps) => {
  const { isEditMode, editingRule } = ownProps;

  let initialValues = {
    name: '',
    discount_percentage: '',
    priority: '',
    subscription_period_months_left: '',
    sales_type: 'RENEWAL',
  };

  if (isEditMode && editingRule) {
    initialValues = {
      name: editingRule.name || '',
      discount_percentage: editingRule.discount_percentage || '',
      priority: editingRule.priority || '',
      sales_type: editingRule.sales_types[0] || 'RENEWAL',
      subscription_period_months_left:
        editingRule.subscription_period_months_left || '',
    };
  }

  return {
    initialValues,
    enableReinitialize: true,
  };
};

export default connect(mapStateToProps)(
  reduxForm({
    form: 'discountRuleEditor',
    validate,
  })(DiscountRuleEditorModal)
);
